import { type DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';

const punctuationCharacters = ['.', '!', '?'];

const estimateTextContentLength = (doc: DocNode, maxLength: number): number => {
	let totalLength = 0;

	try {
		traverse(doc, {
			text: (node) => {
				totalLength += (node.text || '').length;
				// Early termination if we exceed the threshold
				if (totalLength > maxLength) {
					throw new Error('SIZE_EXCEEDED');
				}
			},
			hardBreak: () => {
				totalLength += 1; // Count newlines
				if (totalLength > maxLength) {
					throw new Error('SIZE_EXCEEDED');
				}
			},
		});
	} catch (error) {
		if (error instanceof Error && error.message === 'SIZE_EXCEEDED') {
			return totalLength; // Return the length that exceeded threshold
		}
		// If traversal fails for other reasons, return a conservative estimate
		return maxLength + 1;
	}

	return totalLength;
};

/**
 * Extract plain text from ADF document
 */
const extractTextFromADF = (doc: DocNode): string => {
	const result: string[] = [];

	traverse(doc, {
		text: (node) => {
			result.push(node.text || '');
		},
		hardBreak: () => {
			result.push('\n');
		},
	});

	return result.join('');
};

/**
 * Check if selection starts at a position where capitalization is appropriate
 * (beginning of document, after sentence-ending punctuation, or start of paragraph)
 */
const startsWithSentence = (fullText: string, selectionStart: number): boolean => {
	// Start of document
	if (selectionStart === 0) {
		return true;
	}

	// Look at the text before the selection
	const beforeSelection = fullText.slice(0, selectionStart);

	// Start of paragraph (preceded only by whitespace/newlines)
	if (beforeSelection.trim().length === 0) {
		return true;
	}

	// Check for paragraph break (newline followed by optional whitespace)
	// eslint-disable-next-line require-unicode-regexp
	if (/\n\s*$/.test(beforeSelection)) {
		return true;
	}

	// Check for sentence-ending punctuation followed by optional whitespace
	// eslint-disable-next-line require-unicode-regexp
	if (/[.!?]\s*$/.test(beforeSelection)) {
		return true;
	}

	return false;
};

/**
 * Check if text ends at a sentence boundary
 */
const endsAtSentence = (fullText: string, selectionEnd: number): boolean => {
	const afterSelection = fullText.slice(selectionEnd).trim();
	return afterSelection.length === 0;
};

/**
 * Determine if we need to revert inappropriate API capitalization/punctuation changes
 */
const shouldCorrectCapitalization = (
	originalText: string,
	responseText: string,
	fullDocumentText: string,
	selectionStart: number,
	selectionEnd: number,
): { shouldCorrectFirst: boolean; shouldCorrectLast: boolean } => {
	if (selectionStart === selectionEnd) {
		return { shouldCorrectFirst: false, shouldCorrectLast: false };
	}

	const originalTrimmed = originalText.trim();
	const responseTrimmed = responseText.trim();

	if (!originalTrimmed || !responseTrimmed || originalTrimmed === responseTrimmed) {
		return { shouldCorrectFirst: false, shouldCorrectLast: false };
	}

	const originalFirstChar = originalTrimmed.length > 0 ? getFirstCharacter(originalTrimmed) : '';
	const responseFirstChar = responseTrimmed.length > 0 ? getFirstCharacter(responseTrimmed) : '';
	const originalLastChar = originalTrimmed.length > 0 ? getLastCharacter(originalTrimmed) : '';
	const responseLastChar = responseTrimmed.length > 0 ? getLastCharacter(responseTrimmed) : '';

	// Check if LLM inappropriately capitalized first character (revert to lowercase)
	const shouldCorrectFirst = Boolean(
		originalFirstChar &&
		responseFirstChar &&
		originalFirstChar === originalFirstChar.toLowerCase() && // Original was lowercase
		responseFirstChar === responseFirstChar.toUpperCase() && // LLM made it uppercase
		!startsWithSentence(fullDocumentText, selectionStart), // Not at sentence start
	);

	// Check if LLM inappropriately added punctuation (remove it)
	const shouldCorrectLast = Boolean(
		responseLastChar &&
		punctuationCharacters.includes(responseLastChar) &&
		(!originalLastChar || !punctuationCharacters.includes(originalLastChar)) &&
		!endsAtSentence(fullDocumentText, selectionEnd), // Not at sentence end
	);

	return { shouldCorrectFirst, shouldCorrectLast };
};

/**
 * Get the first character from a string, handling Unicode properly
 */
const getFirstCharacter = (text: string): string => {
	const chars = Array.from(text);
	return chars.length > 0 ? chars[0] : '';
};

/**
 * Get the last character from a string, handling Unicode properly
 */
const getLastCharacter = (text: string): string => {
	const chars = Array.from(text);
	return chars.length > 0 ? chars[chars.length - 1] : '';
};

/**
 * Replace the first character in a string, handling Unicode properly
 */
const replaceFirstCharacter = (text: string, newChar: string): string => {
	const chars = Array.from(text);
	if (chars.length > 0) {
		chars[0] = newChar;
		return chars.join('');
	}
	return text;
};

/**
 * Check if this is the last text node by comparing the extracted text
 */
const isLastMeaningfulTextNode = (doc: DocNode, currentText: string): boolean => {
	const fullText = extractTextFromADF(doc);
	const trimmedFullText = fullText.trimEnd();
	const trimmedCurrentText = currentText.trimEnd();

	// If this node's text appears at the end of the full document text, it's likely the last one
	return trimmedCurrentText.length > 0 && trimmedFullText.endsWith(trimmedCurrentText);
};

/**
 * Revert inappropriate API capitalization and punctuation changes in ADF document
 */
const applyADFCorrections = (
	doc: DocNode,
	shouldCorrectFirst: boolean,
	shouldCorrectLast: boolean,
): DocNode => {
	let firstTextNodeFound = false;
	let correctedDoc = doc;

	correctedDoc = traverse(correctedDoc, {
		text: (node) => {
			if (!node.text) {
				return node;
			}

			let correctedText = node.text;

			// Revert inappropriate capitalization of first character if this is the first text node
			if (!firstTextNodeFound && shouldCorrectFirst && correctedText.trim().length > 0) {
				// Find the first non-whitespace character and its position
				// eslint-disable-next-line require-unicode-regexp
				const firstNonWhitespaceIndex = correctedText.search(/\S/);
				if (firstNonWhitespaceIndex !== -1) {
					const beforeChar = correctedText.slice(0, firstNonWhitespaceIndex);
					const restOfText = correctedText.slice(firstNonWhitespaceIndex);

					// Use Unicode-safe character extraction
					const firstChar = getFirstCharacter(restOfText);
					if (firstChar && firstChar.toUpperCase() !== firstChar.toLowerCase()) {
						const correctedFirstChar = firstChar.toLowerCase();
						const remainingText = replaceFirstCharacter(restOfText, correctedFirstChar);
						correctedText = beforeChar + remainingText;
					}
				}
				firstTextNodeFound = true;
			}

			// Remove inappropriate punctuation only from the last meaningful text node
			if (shouldCorrectLast && isLastMeaningfulTextNode(doc, node.text)) {
				const trimmedText = correctedText.trimEnd();
				if (trimmedText.length > 0) {
					const lastChar = getLastCharacter(trimmedText);
					if (punctuationCharacters.includes(lastChar)) {
						// Use Unicode-safe character removal - remove last character
						const chars = Array.from(trimmedText);
						const textWithoutLastChar = chars.slice(0, -1).join('');

						// Preserve trailing whitespace if any
						const trailingWhitespace = correctedText.slice(trimmedText.length);
						const resultText = textWithoutLastChar + trailingWhitespace;

						correctedText = resultText;
					}
				}
			}

			// Delete the text node if it becomes empty after corrections
			if (correctedText.trim().length === 0) {
				return false;
			}

			return correctedText !== node.text ? { ...node, text: correctedText } : node;
		},
	}) as DocNode;

	return correctedDoc;
};

/**
 * Main function to revert inappropriate API capitalization/punctuation changes based on original selection context.
 * This function only reverts changes - it never adds capitalization or punctuation.
 */
export const correctADFCapitalization = (
	adfResponse: DocNode,
	selectionContext: {
		fullDocumentText: string;
		originalText: string;
		selectionEnd: number;
		selectionStart: number;
	},
): DocNode => {
	try {
		// Performance guard: skip processing for documents with large text content
		const MAX_TEXT_LENGTH = 100000; // ~100KB of text
		const estimatedTextLength = estimateTextContentLength(adfResponse, MAX_TEXT_LENGTH);
		if (estimatedTextLength > MAX_TEXT_LENGTH) {
			return adfResponse;
		}
		// Extract response text from ADF
		const responseText = extractTextFromADF(adfResponse);

		// Use pre-captured selection context
		const { originalText, fullDocumentText, selectionStart, selectionEnd } = selectionContext;

		// Determine what corrections are needed
		const { shouldCorrectFirst, shouldCorrectLast } = shouldCorrectCapitalization(
			originalText,
			responseText,
			fullDocumentText,
			selectionStart,
			selectionEnd,
		);

		if (shouldCorrectFirst || shouldCorrectLast) {
			return applyADFCorrections(adfResponse, shouldCorrectFirst, shouldCorrectLast);
		}

		return adfResponse;
	} catch (error) {
		return adfResponse;
	}
};
